/**
 * Client for FileAPI-bound JWTs minted by AdminServerService. Replaces the
 * old "round-trip to AdminServerService on every file request" path:
 * WebAdminCC asks AdminServerService for a short-lived JWT scoped to a
 * specific instantID, caches it, and attaches `Authorization: Bearer <jwt>`
 * directly to FileAPI fetches. FileAPI validates locally against
 * AdminServerService's JWKS — no per-request hop back to admin.
 *
 * Access token TTL ≈ 5 min (server-controlled); refresh covers the gap with
 * a sliding 24-hour refresh-token chain. Revocation on password reset /
 * admin sign-out-everywhere is handled server-side and surfaces here as a
 * 401 on the next refresh attempt — cache clears and the next request
 * re-mints from scratch.
 */
import { servicePost } from "./client.ts";

interface FileTokenResponse {
  AccessToken: string;
  RefreshToken: string;
  ExpiresAt: string;
  InstantID: string;
}

interface CachedToken {
  accessToken: string;
  refreshToken: string;
  /** Local Date.now() millis when the access token expires. */
  expiresAtMs: number;
  instantID: string;
}

/** Per-instantID token cache. Each instantID is a separate token chain. */
const tokenCache = new Map<string, CachedToken>();

/** Refresh slightly early so a token doesn't expire mid-flight. */
const REFRESH_LEAD_MS = 30_000;

async function mintFresh(instantID: string): Promise<CachedToken> {
  const result = await servicePost<FileTokenResponse>("WebIssueFileAccessToken", { instantID });
  if (result.bError || !result.oData) {
    throw new Error(result.sMessage ?? "Failed to issue file-access token");
  }
  const c: CachedToken = {
    accessToken: result.oData.AccessToken,
    refreshToken: result.oData.RefreshToken,
    expiresAtMs: Date.parse(result.oData.ExpiresAt),
    instantID,
  };
  tokenCache.set(instantID, c);
  return c;
}

async function tryRefresh(cached: CachedToken): Promise<CachedToken | null> {
  const result = await servicePost<FileTokenResponse>("WebRefreshFileAccessToken", {
    refreshToken: cached.refreshToken,
  });
  if (result.bError || !result.oData) {
    return null;
  }
  const c: CachedToken = {
    accessToken: result.oData.AccessToken,
    refreshToken: result.oData.RefreshToken,
    expiresAtMs: Date.parse(result.oData.ExpiresAt),
    instantID: cached.instantID,
  };
  tokenCache.set(cached.instantID, c);
  return c;
}

/**
 * Resolves a current access token for the given instantID. Reuses the cached
 * token while it has ≥ 30s of life; otherwise rotates via the refresh chain;
 * if refresh fails (chain revoked, server error), drops the cache and mints
 * a fresh chain.
 */
export async function getFileAccessToken(instantID: string): Promise<string> {
  if (!instantID) throw new Error("instantID is required");
  const now = Date.now();
  const cached = tokenCache.get(instantID);
  if (cached && cached.expiresAtMs - now > REFRESH_LEAD_MS) {
    return cached.accessToken;
  }
  if (cached) {
    const refreshed = await tryRefresh(cached);
    if (refreshed) return refreshed.accessToken;
    tokenCache.delete(instantID);
  }
  const fresh = await mintFresh(instantID);
  return fresh.accessToken;
}

/**
 * Fetch a FileAPI resource and return a blob: URL suitable for `<img src>` /
 * `<video src>`. The blob URL is owned by the document — caller must call
 * `URL.revokeObjectURL(url)` when the element unmounts to avoid a leak.
 * On 401 (e.g. token revoked mid-render) makes one retry with a freshly
 * minted token.
 */
export async function fetchFileBlobUrl(instantID: string, url: string): Promise<string> {
  let token = await getFileAccessToken(instantID);
  let res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (res.status === 401) {
    tokenCache.delete(instantID);
    token = await getFileAccessToken(instantID);
    res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  }
  if (!res.ok) throw new Error(`File fetch failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Clears every cached file-access token. Call on sign-out so tokens for the
 * previous user don't linger in memory after a new sign-in.
 */
export function clearFileTokenCache(): void {
  tokenCache.clear();
}
